<template>
  <div>
    <v-container>
      <v-row>
        <v-col v-for="item in smallWidget" :key="item.swname">
          <v-card>
            <v-list-item router :to="item.swlink" link>
              <v-list-item-content>
                <div class="text-overline mb-4">
                  {{ item.swname }}
                </div>
                <template v-if="item.swcontent.length == 1">
                  <v-list-item-title class="text-h5 mb-1">
                    {{ item.swcontent[0].swcount }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.swcontent[0].swdesc }}
                  </v-list-item-subtitle>
                </template>
                <template v-else>
                  <v-list-item-title class="text-h5 mb-1">
                    {{ item.swcontent[0].swcount }} - {{ item.swcontent[1].swcount }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.swcontent[0].swdesc }} - {{ item.swcontent[1].swdesc }}
                  </v-list-item-subtitle>
                </template>
              </v-list-item-content>
              <v-list-item-avatar rounded size="80">
                <v-icon size="70" color="#653f5c">{{ item.swicon }}</v-icon>
              </v-list-item-avatar>
            </v-list-item>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col cols="12" class="gcharts-d">
          <v-card>
            <v-row align="center">
              <v-col cols="12" sm="8" md="9">
                <h5 class="py-4 px-4 mb-0">RUNTIME</h5>
                <p class="px-4 text-caption text--secondary mt-0 mb-2">
                  Completed job hours in {{ currentYear }} (from job history)
                </p>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-select
                  v-model="selectedAgentId"
                  class="mx-4"
                  :items="agentSelectItems"
                  item-text="Name"
                  item-value="Id"
                  label="Agents"
                  dense
                  outlined
                  hide-details
                  :disabled="chartsLoading || !agents.length"
                  @change="onAgentFilterChange"
                />
              </v-col>
            </v-row>
            <v-progress-linear v-if="chartsLoading" indeterminate color="primary" />
            <v-subheader v-else-if="runtimeChartEmpty" class="text-center justify-center">
              No completed jobs in {{ currentYear }} yet
            </v-subheader>
            <GChart v-else type="AreaChart" :data="runtimeChartData" :options="runtimeChartOptions" />
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" class="gcharts-d">
          <v-card>
            <h5 class="py-4 px-4">AGENTS</h5>
            <v-progress-linear v-if="chartsLoading" indeterminate color="primary" />
            <v-subheader v-else-if="agents.length === 0" class="justify-center">No agents registered</v-subheader>
            <GChart v-else type="PieChart" :data="chartData" :options="chartOptions" />
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" class="gcharts-d">
          <v-card>
            <h5 class="py-4 px-4">JOBS</h5>
            <p class="px-4 text-caption text--secondary mt-n3 mb-2">RPA job status (all time)</p>
            <v-progress-linear v-if="chartsLoading" indeterminate color="primary" />
            <v-subheader v-else-if="jobsChartEmpty" class="justify-center">No job runs recorded</v-subheader>
            <GChart v-else type="PieChart" :data="chartData2" :options="jobsChartOptions" />
          </v-card>
        </v-col>

        <v-col cols="12" sm="4" class="gcharts-d">
          <v-card>
            <h5 class="py-4 px-4">EXECUTIONS</h5>
            <p class="px-4 text-caption text--secondary mt-n3 mb-2">Workflow runs (Hype)</p>
            <v-progress-linear v-if="chartsLoading" indeterminate color="primary" />
            <v-subheader v-else-if="executionsChartEmpty" class="justify-center">No workflow executions yet</v-subheader>
            <GChart v-else type="PieChart" :data="chartData3" :options="executionsChartOptions" />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import HypeApi from "../hype/api";
import RunApi from "../api";
import { mapState, mapActions } from "vuex";
import { FETCH_AGENTS } from "../store/modules/agent";
import { FETCH_TASK_WITH_PAGE } from "../store/modules/task";
import { FETCH_SCRIPTS } from "../store/modules/script";
import { jobStatusChartColor, jobStatusLabel } from "../utils/jobStatus";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  async created() {
    this.fetchAgents();
    this.fetchScripts();
    this.fetchTasks({ options: {}, isRefreshing: false });
    this.fetchWorkflows();
    this.fetchChatflows();
    await this.loadCharts();
  },
  computed: {
    ...mapState("agent", { agents: (state) => state.agents }),
    ...mapState("script", { scripts: (state) => state.scripts }),
    ...mapState("task", { taskCount: (state) => state.totalCount }),
    ...mapState("workflows", { workflows: (state) => state.workflows }),
    ...mapState("chatflows", { chatflows: (state) => state.flows }),
    ...mapState("account", { planStatus: (state) => state.planStatus }),

    currentYear() {
      return new Date().getFullYear();
    },

    agentSelectItems() {
      return [{ Name: "All agents", Id: null }, ...this.agents];
    },

    smallWidget() {
      return [
        {
          swname: "AGENTS",
          swicon: "laptop_mac",
          swcontent: [{ swdesc: "Total", swcount: this.agents.length }],
          swlink: "/agents",
        },
        {
          swname: "SCRIPTS",
          swicon: "queue_play_next",
          swcontent: [{ swdesc: "Total", swcount: this.scripts.length }],
          swlink: "/scripts",
        },
        {
          swname: "TASKS",
          swicon: "playlist_add_check",
          swcontent: [{ swdesc: "Total", swcount: this.taskCount }],
          swlink: "/tasks",
        },
        {
          swname: "WORKFLOWS",
          swicon: "timeline",
          swcontent: [{ swdesc: "Total", swcount: this.workflows.length }],
          swlink: "/workflows",
        },
        {
          swname: "CHATFLOWS",
          swicon: "chat",
          swcontent: [{ swdesc: "Total", swcount: this.chatflows.length }],
          swlink: "/chatflows",
        },
        {
          swname: "TRANSACTIONS",
          swicon: "playlist_play",
          swcontent: [
            { swdesc: "Used", swcount: this.totalJobs },
            { swdesc: "Remaining", swcount: this.planStatus.remainingJob },
          ],
          swlink: "/jobs",
        },
      ];
    },

    chartData() {
      const connected = this.agents.filter((a) => a.Status === "Connected").length;
      const disconnected = this.agents.length - connected;
      if (!this.agents.length) return [["Status", "Count"]];
      return [
        ["Status", "Count"],
        ["Connected", connected],
        ["Disconnected", disconnected],
      ];
    },

    chartData2() {
      const rows = this.jobStaskStatus
        .filter((j) => j.StatusCount > 0)
        .map((j) => [j.Status, j.StatusCount]);
      if (!rows.length) return [["Status", "Count"]];
      return [["Status", "Count"], ...rows];
    },

    chartData3() {
      const rows = this.executionStatus
        .filter((e) => e.StatusCount > 0)
        .map((e) => [e.Status, e.StatusCount]);
      if (!rows.length) return [["Status", "Count"]];
      return [["Status", "Count"], ...rows];
    },

    jobsChartEmpty() {
      return !this.jobStaskStatus.some((j) => j.StatusCount > 0);
    },

    executionsChartEmpty() {
      return !this.executionStatus.some((e) => e.StatusCount > 0);
    },

    runtimeChartEmpty() {
      if (!this.runtimeChartData || this.runtimeChartData.length < 2) return true;
      return this.runtimeChartData.slice(1).every((row) => !row[1]);
    },

    jobsChartOptions() {
      return {
        ...this.chartOptions2,
        colors: this.jobStaskStatus.map((j) => this.statusColor(j.Status)),
      };
    },

    executionsChartOptions() {
      return {
        ...this.chartOptions3,
        colors: this.executionStatus.map((e) => this.statusColor(e.Status)),
      };
    },

    runtimeChartOptions() {
      const maxH = Math.max(
        1,
        ...this.runtimeChartData.slice(1).map((r) => Number(r[1]) || 0)
      );
      return {
        height: 300,
        title: `Runtime (${this.currentYear})`,
        legend: { position: "none" },
        vAxis: {
          title: "Hours",
          minValue: 0,
          viewWindow: { min: 0, max: Math.ceil(maxH * 1.15) },
          format: "#.##",
        },
        hAxis: { title: "Month" },
        chartArea: { width: "85%", height: "70%" },
      };
    },
  },

  methods: {
    ...mapActions("agent", { fetchAgents: FETCH_AGENTS }),
    ...mapActions("task", { fetchTasks: FETCH_TASK_WITH_PAGE }),
    ...mapActions("script", { fetchScripts: FETCH_SCRIPTS }),
    ...mapActions("workflows", { fetchWorkflows: "loadWorkflows" }),
    ...mapActions("chatflows", { fetchChatflows: "loadFlows" }),

    async loadCharts() {
      this.chartsLoading = true;
      try {
        const [taskStatus, executionStatus, jobFilter] = await Promise.all([
          RunApi.getTaskStatus().catch(() => []),
          HypeApi.getTotalExecutionsCount().catch(() => []),
          this.loadYearJobs(),
        ]);

        this.jobStaskStatus = (taskStatus || []).map((job) => ({
          Status: jobStatusLabel(job.Status),
          StatusCount: Number(job.StatusCount) || 0,
        }));

        this.executionStatus = (executionStatus || []).map((row) => ({
          Status: this.normalizeExecutionStatus(row.Status),
          StatusCount: Number(row.StatusCount) || 0,
        }));

        this.jobs = jobFilter;
        this.totalJobs = this.jobStaskStatus.reduce((t, item) => t + item.StatusCount, 0);
        this.runtimeChartData = this.buildMonthlyRuntime(this.selectedAgentId);
      } catch (e) {
        console.error("[Dashboard]", e);
      } finally {
        this.chartsLoading = false;
      }
    },

    async loadYearJobs() {
      const year = this.currentYear;
      const start = new Date(year, 0, 1);
      const end = new Date(year, 11, 31, 23, 59, 59, 999);
      try {
        const data = await RunApi.filterJobs({
          page: 1,
          itemsPerPage: 5000,
          startDateTime: start,
          endDateTime: end,
        });
        const rows = data?.rows || data || [];
        return rows
          .filter((j) => j.FinishedAt && j.StartedAt)
          .map((j) => ({
            AgentId: j.AgentId,
            StartedAt: j.StartedAt,
            FinishedAt: j.FinishedAt,
          }));
      } catch (_) {
        const list = await RunApi.listJobs().catch(() => []);
        return (list || [])
          .filter((j) => j.FinishedAt && j.StartedAt)
          .filter((j) => new Date(j.StartedAt).getFullYear() === year)
          .map((j) => ({
            AgentId: j.AgentId,
            StartedAt: j.StartedAt,
            FinishedAt: j.FinishedAt,
          }));
      }
    },

    jobRuntimeHours(job) {
      const start = new Date(job.StartedAt);
      const end = new Date(job.FinishedAt);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
        return 0;
      }
      return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    },

    buildMonthlyRuntime(agentId) {
      const totals = new Array(12).fill(0);
      const year = this.currentYear;

      for (const job of this.jobs) {
        if (agentId != null && job.AgentId !== agentId) continue;
        const start = new Date(job.StartedAt);
        if (start.getFullYear() !== year) continue;
        totals[start.getMonth()] += this.jobRuntimeHours(job);
      }

      const data = [["Month", "Runtime (h)"]];
      MONTH_LABELS.forEach((label, i) => {
        data.push([label, Math.round(totals[i] * 100) / 100]);
      });
      return data;
    },

    onAgentFilterChange() {
      this.runtimeChartData = this.buildMonthlyRuntime(this.selectedAgentId);
    },

    statusColor(status) {
      return jobStatusChartColor(status);
    },

    getStatusText(status) {
      return jobStatusLabel(status);
    },

    normalizeExecutionStatus(status) {
      const s = String(status || "").toLowerCase();
      if (s === "success" || s === "succeeded") return "Success";
      if (s === "error" || s === "failed" || s === "failure") return "Failure";
      if (s === "running") return "Running";
      if (s === "waiting") return "Waiting";
      if (s === "canceled" || s === "cancelled") return "Canceled";
      if (!status) return "Unknown";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
  },

  data() {
    return {
      chartsLoading: true,
      selectedAgentId: null,
      runtimeChartData: [["Month", "Runtime (h)"]],
      chartOptions: {
        colors: ["#4CAF50", "#D50000"],
        height: 300,
        pieHole: 0.4,
        legend: { position: "bottom" },
      },
      chartOptions2: {
        height: 300,
        pieHole: 0.4,
        legend: { position: "bottom" },
      },
      chartOptions3: {
        height: 300,
        pieHole: 0.4,
        legend: { position: "bottom" },
      },
      jobs: [],
      jobStaskStatus: [],
      executionStatus: [],
      totalJobs: 0,
    };
  },
};
</script>
